import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  HomeOutlined,
  InboxOutlined,
  RobotOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  ApiOutlined,
  CrownOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { Avatar, Tag } from 'antd';
import { useAuth } from '../../contexts/AuthContext';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useCreditBalance } from '../../hooks/useCreditBalance';
import MobileSheet from './MobileSheet';
import { replyPostsService } from '../../services/replyPostsService';
import { USER_TYPE_LABELS, USER_TYPE_COLORS } from '../../config/supertokens';

interface NavTab {
  key: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  badge?: number;
}

function useInboxBadge(user: any) {
  const [count, setCount] = useState(0);

  const refresh = useCallback(async () => {
    if (!user) { setCount(0); return; }
    try {
      const data = await replyPostsService.getPostsWithReplies(50, 0, 'pending');
      setCount(Array.isArray(data) ? data.length : 0);
    } catch {
      // silently ignore
    }
  }, [user]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 30_000);
    return () => clearInterval(id);
  }, [refresh]);

  return count;
}

function getActivePath(pathname: string): string {
  if (pathname === '/dashboard' || pathname === '/') return '/dashboard';
  if (
    pathname.startsWith('/inbox') ||
    pathname.includes('/inbox')
  ) return '/inbox';
  if (
    pathname.startsWith('/ai-reply-generator') ||
    pathname.startsWith('/tools/ai-reply-generator')
  ) return '/ai-reply';
  return '';
}

const AppBottomNav: React.FC = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isSuperAdmin } = useAuth();
  const { balance: creditBalance } = useCreditBalance();
  const inboxCount = useInboxBadge(user);
  const [moreOpen, setMoreOpen] = useState(false);

  const brandIdFromPath = location.pathname.match(/^\/brands\/([^/]+)/)?.[1] ?? null;

  const inboxPath = brandIdFromPath
    ? `/brands/${brandIdFromPath}/inbox`
    : '/inbox';

  const activeKey = getActivePath(location.pathname);
  // Also treat "more" as active if we're on a settings/credits/etc path
  const isMoreActive = activeKey === '';

  const tabs: NavTab[] = [
    {
      key: '/dashboard',
      label: 'Home',
      icon: <HomeOutlined />,
      path: '/dashboard',
    },
    {
      key: '/inbox',
      label: 'Inbox',
      icon: <InboxOutlined />,
      path: inboxPath,
      badge: inboxCount,
    },
    {
      key: '/ai-reply',
      label: 'AI Reply',
      icon: <RobotOutlined />,
      path: '/tools/ai-reply-generator',
    },
  ];

  const handleLogout = async () => {
    setMoreOpen(false);
    await logout();
    navigate('/');
  };

  if (!isMobile || !user) return null;

  return (
    <>
      <nav className="app-bottom-nav">
        <div className="flex items-stretch justify-around gap-1 flex-1 px-1">
          {tabs.map((tab) => {
            const isActive = activeKey === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => navigate(tab.path)}
                className="relative flex-1 flex flex-col items-center justify-center py-2 px-1 rounded-2xl text-xs font-medium transition-colors min-w-0"
                style={{ color: isActive ? '#0284c7' : '#64748b' }}
                aria-label={tab.label}
              >
                {isActive && (
                  <motion.div
                    layoutId="appNavPill"
                    className="absolute inset-0 rounded-2xl"
                    style={{
                      background:
                        'linear-gradient(135deg, rgba(2,132,199,0.10), rgba(99,102,241,0.10))',
                      border: '1px solid rgba(2,132,199,0.18)',
                    }}
                    transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                  />
                )}
                <motion.div
                  className="relative flex flex-col items-center"
                  animate={{ y: isActive ? -2 : 0, scale: isActive ? 1.05 : 1 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 28 }}
                >
                  <div className="relative text-xl leading-none mb-0.5">
                    {tab.icon}
                    {tab.badge && tab.badge > 0 ? (
                      <span
                        className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white flex items-center justify-center"
                        style={{ background: '#ef4444' }}
                      >
                        {tab.badge > 99 ? '99+' : tab.badge}
                      </span>
                    ) : null}
                  </div>
                  <span className="leading-tight truncate">{tab.label}</span>
                </motion.div>
              </button>
            );
          })}

          {/* More button */}
          <button
            onClick={() => setMoreOpen(true)}
            className="relative flex-1 flex flex-col items-center justify-center py-2 px-1 rounded-2xl text-xs font-medium transition-colors min-w-0"
            style={{ color: isMoreActive ? '#0284c7' : '#64748b' }}
            aria-label="More"
          >
            {isMoreActive && (
              <motion.div
                layoutId="appNavPill"
                className="absolute inset-0 rounded-2xl"
                style={{
                  background:
                    'linear-gradient(135deg, rgba(2,132,199,0.10), rgba(99,102,241,0.10))',
                  border: '1px solid rgba(2,132,199,0.18)',
                }}
                transition={{ type: 'spring', stiffness: 500, damping: 35 }}
              />
            )}
            <motion.div
              className="relative flex flex-col items-center"
              animate={{ y: isMoreActive ? -2 : 0, scale: isMoreActive ? 1.05 : 1 }}
              transition={{ type: 'spring', stiffness: 400, damping: 28 }}
            >
              <div className="relative text-xl leading-none mb-0.5">
                <Avatar
                  icon={<UserOutlined />}
                  size={22}
                  className="bg-gray-100 text-gray-600"
                />
              </div>
              <span className="leading-tight">More</span>
            </motion.div>
          </button>
        </div>
      </nav>

      {/* More sheet */}
      <MobileSheet open={moreOpen} onClose={() => setMoreOpen(false)} title="Menu" zIndex={300}>
        <div className="px-5 pb-5">
          {/* User info */}
          <div className="flex items-center gap-3 py-4 border-b border-gray-100 mb-3">
            <Avatar
              icon={<UserOutlined />}
              size={48}
              className="bg-primary-100 text-primary-600 flex-shrink-0"
            />
            <div className="min-w-0">
              <div className="font-semibold text-gray-900 truncate">{user?.name}</div>
              <div className="text-xs text-gray-500 truncate">{user?.email}</div>
              <Tag
                color={
                  USER_TYPE_COLORS[user?.user_type as keyof typeof USER_TYPE_COLORS] || 'default'
                }
                className="mt-1 text-xs"
              >
                {USER_TYPE_LABELS[user?.user_type as keyof typeof USER_TYPE_LABELS] ||
                  user?.user_type}
              </Tag>
            </div>
          </div>

          {/* Credits */}
          {creditBalance !== null && (
            <button
              onClick={() => { setMoreOpen(false); navigate('/credits'); }}
              className="w-full flex items-center gap-3 px-3 py-3.5 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-colors tap-scale text-left mb-1"
            >
              <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                <WalletOutlined className="text-blue-600" />
              </div>
              <div>
                <div className="text-sm font-medium text-gray-900">Credits</div>
                <div className={`text-xs ${creditBalance > 0 ? 'text-blue-600' : 'text-red-500'}`}>
                  {creditBalance.toLocaleString()} remaining
                </div>
              </div>
            </button>
          )}

          {/* Settings */}
          <button
            onClick={() => {
              setMoreOpen(false);
              if (brandIdFromPath) navigate(`/brands/${brandIdFromPath}/settings`);
              else navigate('/dashboard');
            }}
            className="w-full flex items-center gap-3 px-3 py-3.5 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-colors tap-scale text-left mb-1"
          >
            <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
              <SettingOutlined className="text-gray-600" />
            </div>
            <div className="text-sm font-medium text-gray-900">Settings</div>
          </button>

          {/* MCP Integration */}
          <button
            onClick={() => { setMoreOpen(false); navigate('/settings/mcp'); }}
            className="w-full flex items-center gap-3 px-3 py-3.5 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-colors tap-scale text-left mb-1"
          >
            <div className="w-9 h-9 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0">
              <ApiOutlined className="text-purple-600" />
            </div>
            <div className="text-sm font-medium text-gray-900">MCP Integration</div>
          </button>

          {/* Admin Panel (super admin only) */}
          {isSuperAdmin && (
            <button
              onClick={() => { setMoreOpen(false); navigate('/admin'); }}
              className="w-full flex items-center gap-3 px-3 py-3.5 rounded-xl hover:bg-amber-50 active:bg-amber-100 transition-colors tap-scale text-left mb-1"
            >
              <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                <CrownOutlined className="text-amber-500" />
              </div>
              <div className="text-sm font-medium text-amber-700">Admin Panel</div>
            </button>
          )}

          {/* Divider */}
          <div className="border-t border-gray-100 mt-2 pt-2">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-3.5 rounded-xl hover:bg-red-50 active:bg-red-100 transition-colors tap-scale text-left"
            >
              <div className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                <LogoutOutlined className="text-red-500" />
              </div>
              <div className="text-sm font-medium text-red-600">Log out</div>
            </button>
          </div>
        </div>
      </MobileSheet>
    </>
  );
};

export default AppBottomNav;
